import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../api';

const API_URL = 'https://restaurant-backend-pi-nine.vercel.app';

// Cloudinary delivery optimization: auto WebP/AVIF + auto quality + width cap (never upscales).
// Cuts delivered image weight ~5-10x. Touches only Cloudinary /upload/ URLs; others pass through.
function cldOpt(url, w = 800) {
  if (!url || !url.includes('res.cloudinary.com') || !url.includes('/upload/')) return url;
  if (/\/upload\/[^/]*(f_auto|q_auto)/.test(url)) return url;
  return url.replace('/upload/', `/upload/f_auto,q_auto,c_limit,w_${w}/`);
}

function useIsMobile() {
  const [v, setV] = useState(window.innerWidth < 768);
  useEffect(() => {
    const fn = () => setV(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return v;
}

const TelegramIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"></line>
    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
  </svg>
);

const FacebookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
  </svg>
);

const ViberIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
  </svg>
);

const LinkIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
  </svg>
);

const ShareOutlineIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3"></circle>
    <circle cx="6" cy="12" r="3"></circle>
    <circle cx="18" cy="19" r="3"></circle>
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
  </svg>
);

function LanchCard({ dish }) {
  const isMobile = useIsMobile();
  const [liked, setLiked] = useState(() => localStorage.getItem(`liked_${dish.id}`) === 'true');
  const [modal, setModal] = useState(false);
  const [shareMenu, setShareMenu] = useState(false);

  const imgSrc = dish.image_url ? cldOpt(dish.image_url.startsWith('http') ? dish.image_url : `${API_URL}${dish.image_url}`, 800) : null;
  const currentPrice = dish.discount_price ? Number(dish.discount_price) : Number(dish.price);

  const handleLike = (e) => {
    e.stopPropagation();
    const n = !liked;
    setLiked(n);
    localStorage.setItem(`liked_${dish.id}`, n);
    window.dispatchEvent(new Event('likeUpdated'));
  };

  const shareLinks = [
    { name: 'Telegram', icon: <TelegramIcon />, url: `https://t.me/share/url?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(dish.name)}` },
    { name: 'Facebook', icon: <FacebookIcon />, url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}` },
    { name: 'Viber', icon: <ViberIcon />, url: `viber://forward?text=${encodeURIComponent(dish.name + ' ' + window.location.href)}` },
    { name: 'Копіювати', icon: <LinkIcon />, url: null },
  ];

  const handleShareClick = async (item, e) => {
    e.stopPropagation();
    if (item === 'native') {
      if (isMobile && navigator.share) {
        try { await navigator.share({ title: dish.name, url: window.location.href }); } catch {}
      } else {
        setShareMenu(true);
      }
      return;
    }
    if (!item.url) { 
      navigator.clipboard.writeText(window.location.href); 
      alert('Посилання скопійовано!'); 
    } else { 
      window.open(item.url, '_blank'); 
    }
    setShareMenu(false);
  };

  const renderShareMenu = () => {
    if (!shareMenu) return null;
    return createPortal(
      <div style={{
        position: 'fixed', inset: 0, zIndex: 2147483648,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)'
      }} onClick={(e) => { e.stopPropagation(); setShareMenu(false); }}>
        <div style={{
          background: '#fff', borderRadius: 24, padding: '28px 32px',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20,
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
        }} onClick={e => e.stopPropagation()}>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 24, color: '#0d0d0b' }}>Поділитися</div>
          <div style={{ display: 'flex', gap: 16 }}>
            {shareLinks.map(item => (
              <button key={item.name} onClick={(e) => handleShareClick(item, e)} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                background: 'transparent', border: 'none', cursor: 'pointer'
              }}>
                <div style={{ 
                  width: 52, height: 52, borderRadius: '50%', background: '#fff', 
                  border: '1px solid #e8ddd0', display: 'flex', alignItems: 'center', 
                  justifyContent: 'center', color: '#0d0d0b', transition: 'all 0.2s',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
                }} onMouseEnter={e => { e.currentTarget.style.borderColor = '#d4af37'; e.currentTarget.style.color = '#d4af37'; e.currentTarget.style.transform = 'translateY(-2px)'; }} onMouseLeave={e => { e.currentTarget.style.borderColor = '#e8ddd0'; e.currentTarget.style.color = '#0d0d0b'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                  {item.icon}
                </div>
                <span style={{ fontSize: 12, color: '#6b6560', fontWeight: 500 }}>{item.name}</span>
              </button>
            ))}
          </div>
          <button onClick={(e) => { e.stopPropagation(); setShareMenu(false); }} style={{
            marginTop: 12, padding: '10px 32px', borderRadius: 12, background: '#f4ece1', 
            border: 'none', color: '#0d0d0b', fontWeight: 500, cursor: 'pointer', transition: 'background 0.2s'
          }} onMouseEnter={e => e.currentTarget.style.background = '#e8ddd0'} onMouseLeave={e => e.currentTarget.style.background = '#f4ece1'}>
            Скасувати
          </button>
        </div>
      </div>,
      document.body
    );
  };

  const descLines = (dish.description || '').split('\n').filter(l => l.trim());

  return (
    <>
      <div
        onClick={() => setModal(true)}
        style={{
          background: '#fff',
          borderRadius: 18,
          border: '1px solid #e8ddd0',
          overflow: 'hidden',
          cursor: 'pointer',
          transition: 'transform 0.18s, box-shadow 0.18s',
          boxShadow: '0 2px 12px rgba(13,13,11,0.06)',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(13,13,11,0.11)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(13,13,11,0.06)'; }}
      >
        <div style={{
          width: '100%',
          height: isMobile ? 160 : 200,
          background: 'linear-gradient(135deg, #e8ddd0 0%, #f0e8dc 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: isMobile ? 52 : 64,
          overflow: 'hidden',
          position: 'relative',
        }}>
          {imgSrc
            ? <img src={imgSrc} alt={dish.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <span style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))' }}>{dish.emoji || '🍽'}</span>
          }
          
          {dish.discount_price && (
            <div style={{ position: 'absolute', top: 12, left: 12, background: '#e74c3c', color: '#fff', fontSize: 11, fontWeight: 700, padding: '5px 12px', borderRadius: 20, boxShadow: '0 4px 12px rgba(231,76,60,0.3)', letterSpacing: '0.5px' }}>
              АКЦІЯ %
            </div>
          )}

          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            height: 60,
            background: 'linear-gradient(to top, rgba(255,255,255,0.9), transparent)',
          }} />
          <div style={{
            position: 'absolute', top: 12, right: 12,
            background: dish.discount_price ? '#e74c3c' : 'rgba(13,13,11,0.75)',
            backdropFilter: 'blur(8px)',
            color: dish.discount_price ? '#fff' : '#e8c97e',
            fontFamily: "'Fraunces', serif",
            fontSize: 15, fontWeight: 600,
            padding: '5px 12px', borderRadius: 20,
            display: 'flex', flexDirection: 'column', alignItems: 'flex-end'
          }}>
            {dish.discount_price && <span style={{ fontSize: 10, textDecoration: 'line-through', color: 'rgba(255,255,255,0.7)', marginBottom: -2 }}>{dish.price} ₴</span>}
            <span>
              {currentPrice} ₴
              {dish.weight && <span style={{ fontSize: 11, color: dish.discount_price ? 'rgba(255,255,255,0.8)' : 'rgba(232, 201, 126, 0.7)', marginLeft: 4, fontFamily: 'sans-serif' }}>/ {dish.weight}</span>}
            </span>
          </div>
        </div>

        <div style={{ padding: isMobile ? '14px 16px 16px' : '16px 20px 18px' }}>
          <div style={{
            fontFamily: "'Fraunces', serif",
            fontSize: isMobile ? 18 : 20,
            fontWeight: 500, color: '#0d0d0b',
            marginBottom: 8, lineHeight: 1.2,
          }}>
            {dish.name}
          </div>

          {descLines.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}>
              {descLines.map((line, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                  fontSize: 13, color: '#6b6560', lineHeight: 1.4, fontWeight: 300,
                }}>
                  <div style={{
                    width: 5, height: 5, borderRadius: '50%',
                    background: '#c8b89a', flexShrink: 0, marginTop: 6,
                  }} />
                  {line}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: '#6b6560', lineHeight: 1.5, marginBottom: 14, fontWeight: 300 }}>
              {dish.description}
            </div>
          )}

          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
            gap: 8, paddingTop: 10,
            borderTop: '1px solid #f0e8dc',
          }} onClick={e => e.stopPropagation()}>
            <button onClick={(e) => handleShareClick('native', e)} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', borderRadius: 20,
              border: '1px solid #e8ddd0', background: '#faf7f3',
              fontSize: 12, color: '#6b6560', cursor: 'pointer',
              transition: 'all 0.2s',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#d4af37'; e.currentTarget.style.color = '#d4af37'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e8ddd0'; e.currentTarget.style.color = '#6b6560'; }}
            >
              <ShareOutlineIcon /> Поділитись
            </button>

            <button onClick={handleLike} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', borderRadius: 20,
              border: `1px solid ${liked ? '#fde8e8' : '#e8ddd0'}`,
              background: liked ? '#fff5f5' : '#faf7f3',
              fontSize: 12,
              color: liked ? '#e74c3c' : '#6b6560',
              cursor: 'pointer', transition: 'all 0.15s',
            }}>
              <span style={{ fontSize: 14 }}>{liked ? '❤️' : '🤍'}</span>
              {liked ? 'В обраному' : 'В обране'}
            </button>
          </div>
        </div>
      </div>

      {modal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
          display: 'flex', alignItems: isMobile ? 'flex-end' : 'center',
          justifyContent: 'center', zIndex: 200, padding: isMobile ? 0 : 20,
        }} onClick={() => setModal(false)}>
          <div style={{
            background: '#fff',
            borderRadius: isMobile ? '20px 20px 0 0' : 20,
            width: isMobile ? '100%' : 460,
            maxHeight: isMobile ? '88vh' : '85vh',
            overflow: 'hidden', display: 'flex', flexDirection: 'column',
          }} onClick={e => e.stopPropagation()}>

            <div style={{
              height: isMobile ? 200 : 240,
              background: '#e8ddd0', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 72, overflow: 'hidden', position: 'relative',
            }}>
              {imgSrc
                ? <img src={imgSrc} alt={dish.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <span>{dish.emoji || '🍽'}</span>
              }
              {isMobile && <div style={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', width: 36, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.5)' }} />}
              <button onClick={() => setModal(false)} style={{
                position: 'absolute', top: 12, right: 12,
                width: 32, height: 32, borderRadius: '50%',
                background: 'rgba(0,0,0,0.4)', border: 'none',
                color: '#fff', fontSize: 16, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>✕</button>
            </div>

            <div style={{ padding: '20px 22px 28px', overflowY: 'auto' }}>
              <div style={{ fontFamily: "'Fraunces', serif", fontSize: 22, fontWeight: 500, marginBottom: 10 }}>
                {dish.name}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
                {descLines.map((line, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 14, color: '#6b6560', lineHeight: 1.5 }}>
                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#c8b89a', flexShrink: 0, marginTop: 8 }} />
                    {line}
                  </div>
                ))}
                {descLines.length === 0 && (
                  <div style={{ fontSize: 14, color: '#6b6560', lineHeight: 1.7 }}>{dish.description}</div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontFamily: "'Fraunces', serif", fontSize: 26, fontWeight: 600, color: dish.discount_price ? '#e74c3c' : '#0d0d0b' }}>
                    {currentPrice} ₴
                  </span>
                  {dish.discount_price && (
                    <span style={{ fontSize: 16, color: '#b5a898', textDecoration: 'line-through', fontFamily: "'Fraunces', serif" }}>{dish.price} ₴</span>
                  )}
                  {dish.weight && <span style={{ fontSize: 14, color: '#8c857d', fontWeight: 400, fontFamily: 'sans-serif', marginLeft: 2 }}>/ {dish.weight}</span>}
                </div>

                <button onClick={handleLike} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  border: '1px solid #ddd5c8', borderRadius: 20,
                  padding: '8px 16px', background: 'transparent',
                  fontSize: 13, cursor: 'pointer',
                  color: liked ? '#e74c3c' : '#6b6560',
                }}>
                  {liked ? '❤️' : '🤍'} {liked ? 'В обраному' : 'В обране'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export const LanchPage = () => {
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    api.get('/dishes')
      .then(r => setDishes(r.data))
      .catch(() => setDishes([]))
      .finally(() => setLoading(false));
  }, []);

  const lanchDishes = dishes.filter(d => String(d.category_id) === '8');

  return (
    <div className="page-enter" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      <div style={{
        background: '#0d0d0b',
        padding: isMobile ? '22px 20px 20px' : '32px 40px 28px',
        flexShrink: 0, position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', right: -20, top: -20, width: 160, height: 160, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.06)', pointerEvents: 'none' }} />

        <div style={{ fontSize: 10, letterSpacing: '2.5px', textTransform: 'uppercase', color: '#c8b89a', marginBottom: 10, fontWeight: 300 }}>
          Щоденно з 12:00 до 14:30
        </div>
        
        <div style={{ fontFamily: "'Fraunces', serif", fontSize: isMobile ? 32 : 42, color: '#fff', lineHeight: 1.1, fontWeight: 300 }}>
          Ланч <span style={{ 
            fontStyle: 'italic', 
            fontWeight: 600,
            background: 'linear-gradient(90deg, #F9E79F 0%, #D4AF37 50%, #F9E79F 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 4px 20px rgba(212, 175, 55, 0.4)',
            display: 'inline-block',
            paddingRight: '5px' 
          }}>щодня</span>
        </div>
        
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', marginTop: 8, fontWeight: 300 }}>
          Комплексні обіди · Меню оновлюється щомісяця
        </div>

        <div style={{
          display: 'flex', gap: 24, marginTop: 18,
          paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.1)',
        }}>
          {[['🕐', 'з 12:00 до 14:30'], ['🍽', 'Комплексний обід'], ['💰', 'Фіксована ціна · 220 ₴']].map(([emoji, label]) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 14 }}>{emoji}</span>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', fontWeight: 300 }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{
        flex: 1, overflowY: 'auto',
        padding: isMobile ? '14px 12px 20px' : '24px 32px 32px',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
          gap: isMobile ? 12 : 16,
          maxWidth: 900, margin: '0 auto',
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#6b6560', gridColumn: '1/-1' }}>
              Завантаження меню...
            </div>
          ) : lanchDishes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b6560', fontSize: 14, gridColumn: '1/-1' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🍽</div>
              <div style={{ fontFamily: "'Fraunces', serif", fontSize: 20, color: '#0d0d0b', marginBottom: 8 }}>
                Ланчі скоро з'являться
              </div>
              <div>Меню оновлюється щомісяця</div>
            </div>
          ) : (
            lanchDishes.map(dish => <LanchCard key={dish.id} dish={dish} />)
          )}
        </div>
      </div>
    </div>
  );
};
